import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { Link, useRouter } from '@tanstack/react-router';
import {
	type DeleteGroupByIdParams,
	ShipmentStatusCountShipmentStatus,
} from '@uturn/api/v1';
import type { GetShipmentsFilterStatusesItem } from '@uturn/api/v2';
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Icon,
} from '@uturn/ui-kit';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	CancelShipmentsException,
	ShipmentsCancelDropdownMenuItem,
	ShipmentsCancelModal,
} from '../shipments-cancel';
import {
	ShipmentsExportDropdownMenuItem,
	ShipmentsExportModal,
} from '../shipments-export';
import { PICKED_SHIPMENT_STATUSES } from './constants';
import { RenameShipmentGroupModal } from './rename-shipment-group-modal';
import type {
	onClickProps,
	ShipmentGroupCardProps,
} from './shipment-group-card.types';
import { ShipmentGroupStatusTile } from './shipment-group-status-tile';
import { getDefaultShipmentStatusLinkProps, getShipmentCount } from './utils';

export function ShipmentGroupCard({
	id,
	name,
	shipmentStatusCount,
	groupsQueryKey,
	onClick,
}: ShipmentGroupCardProps) {
	const { t } = useTranslation();
	const router = useRouter();

	const [openRenameModal, setOpenRenameModal] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openCancelModal, setOpenCancelModal] = useState(false);

	const totalShipments = getShipmentCount(shipmentStatusCount);

	const archivedShipments = getShipmentCount(shipmentStatusCount, [
		ShipmentStatusCountShipmentStatus.COMPLETED,
		ShipmentStatusCountShipmentStatus.CANCELLED,
	]);
	// COST_DECLARED is also non-cancellable in bulk, but the v1
	// `ShipmentStatusCount` enum returned by GET /groups doesn't include it
	// yet — see the TODO in ./constants.ts. Once the backend surfaces it
	// here, add `ShipmentStatusCountShipmentStatus.COST_DECLARED` to this
	// array to keep the per-group card consistent with the layout-level
	// non-cancellable count (which already covers both).
	const nonCancellableShipments = getShipmentCount(shipmentStatusCount, [
		ShipmentStatusCountShipmentStatus.EXECUTED,
	]);
	const cancellableShipments =
		totalShipments - archivedShipments - nonCancellableShipments;

	const linkProps = getDefaultShipmentStatusLinkProps(id);

	const handleOnClick = ({ event, to, params, search }: onClickProps) => {
		event.preventDefault();
		onClick?.();

		if (event.ctrlKey || event.metaKey) {
			return window.open(
				router.buildLocation({ to, params, search }).href,
				'_blank',
			);
		}

		router.navigate({ to, params, search });
	};

	return (
		<>
			<Card className="relative flex h-min cursor-pointer flex-col transition-shadow duration-300 hover:shadow-md dark:hover:bg-muted/20">
				<CardHeader className="flex flex-row items-start justify-between space-y-0">
					<Link
						{...linkProps}
						className='after:content-[" "] flex flex-col gap-1 after:absolute after:inset-0'
						onClick={(event) => handleOnClick({ ...linkProps, event })}
					>
						<CardTitle>{name}</CardTitle>
						<CardDescription>
							{t('pages.shipments.groups.overview.card.total-shipments', {
								defaultValue: '{{count}} shipments',
								count: totalShipments,
							})}
						</CardDescription>
					</Link>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="secondary" size="icon" className="z-10">
								<Icon icon={faEllipsisVertical} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => setOpenRenameModal(true)}>
								{t(
									'pages.shipments.groups.overview.card.menu.rename-group',
									'Rename',
								)}
							</DropdownMenuItem>
							<ShipmentsExportDropdownMenuItem
								setOpenModal={setOpenExportModal}
							/>
							<ShipmentsCancelDropdownMenuItem
								setOpenModal={setOpenCancelModal}
								cancelShipmentsException={
									totalShipments === 0
										? CancelShipmentsException.NO_SHIPMENTS
										: undefined
								}
								disabled={totalShipments > 0 && cancellableShipments === 0}
								disabledTooltip={t(
									'pages.shipments_cancel.dropdown.executed-only-disabled',
									'Selected shipments cannot be canceled as they are already executed.',
								)}
							/>
						</DropdownMenuContent>
					</DropdownMenu>
				</CardHeader>

				<CardContent className="flex flex-wrap gap-2">
					{PICKED_SHIPMENT_STATUSES.map((shipmentStatus) => {
						const key = `${id}-${shipmentStatus}`;

						return (
							<ShipmentGroupStatusTile
								key={key}
								id={id}
								shipmentsStatusCount={shipmentStatusCount}
								shipmentStatus={shipmentStatus}
								handleOnClick={handleOnClick}
							/>
						);
					})}
				</CardContent>
			</Card>
			<RenameShipmentGroupModal
				group={{ id, name }}
				groupsQueryKey={groupsQueryKey}
				openModal={openRenameModal}
				setOpenModal={setOpenRenameModal}
			/>
			<ShipmentsExportModal
				exportFilters={{ 'filter.groups': [id] }}
				isOpenModal={openExportModal}
				setOpenModal={setOpenExportModal}
				totalShipments={totalShipments}
			/>
			<ShipmentsCancelModal
				groupId={id}
				exportFilters={{} as DeleteGroupByIdParams}
				statuses={[] as GetShipmentsFilterStatusesItem[]}
				isOpenModal={openCancelModal}
				setOpenModal={setOpenCancelModal}
				totalGroupShipments={cancellableShipments}
				nonCancellableCount={nonCancellableShipments}
			/>
		</>
	);
}
